import type { ReportDataTableColumn } from '../../components/ReportDataTable'
import { formatAmount } from '@/lib/utils/formatters'
import { Link } from 'react-router-dom'
import { Eye, Pencil } from 'lucide-react'
import { ROUTE_BUILDERS } from '@/config/routes'
import type { ConfiguredReportReportRow } from '../../types/pages'
import { display, makeLink, formatDate, applicantId } from '../../utils/configuredReportFormatters'
import reportStyles from '../../styles/reportsTable.module.css'
import { cn } from '@lib/utils/helpers'

/** Shared center alignment for Chargeback list headers + cells. */
const headerCenter = (minWidth: string) =>
  cn(minWidth, 'text-center !text-center')
const cellCenter = (...parts: string[]) =>
  cn(...parts, 'text-center !text-center align-middle')

export const chargebackColumns: ReportDataTableColumn<ConfiguredReportReportRow, string>[] = [
  {
    id: 'pa_id',
    label: 'Log Id',
    sortKey: 'pa_id',
    headerClassName: headerCenter('min-w-[5.5rem]'),
    className: cellCenter(
      'whitespace-nowrap font-mono text-xs tabular-nums text-slate-700',
    ),
    render: (row) => {
      const logId = row.pa_id ?? row.payment_adjustment_id
      if (logId == null || logId === '') return '—'
      return (
        <span className="inline-block w-full text-center font-mono text-xs tabular-nums text-slate-700">
          {display(logId)}
        </span>
      )
    },
  },
  {
    id: 'company_name',
    label: 'Company',
    sortKey: 'company_name',
    headerClassName: headerCenter('min-w-[10rem]'),
    className: cellCenter(
      'min-w-[10rem] whitespace-normal font-medium text-slate-900',
    ),
    render: (row) => display(row.company_name),
  },
  {
    id: 'property_name',
    label: 'Property',
    sortKey: 'property_name',
    headerClassName: headerCenter('min-w-[11rem]'),
    className: cellCenter(
      'min-w-[11rem] whitespace-normal font-medium text-slate-900',
    ),
    render: (row) => display(row.property_name),
  },
  {
    id: 'applicant_id',
    label: 'Applicant ID',
    sortKey: 'applicant_id',
    headerClassName: headerCenter('min-w-[7.5rem]'),
    className: cellCenter(
      'whitespace-nowrap font-mono text-xs text-[#1597ff]',
    ),
    render: (row) => {
      const id = row.applicant_id ?? row.fk_applicant_id
      return id
        ? makeLink(
            `${ROUTE_BUILDERS.DASHBOARD_APPLICATION_DETAIL(String(id))}#payment`,
            applicantId(id),
            true,
          )
        : '—'
    },
  },
  {
    id: 'name',
    label: 'Name',
    headerClassName: headerCenter('min-w-[9.5rem]'),
    className: cellCenter(
      'min-w-[9.5rem] whitespace-normal font-medium text-slate-800',
    ),
    render: (row) =>
      `${display(row.first_name)} ${display(row.last_name)}`
        .replace(/—/g, '')
        .trim() || display(row.name),
  },
  {
    id: 'type',
    label: 'Adj. Type',
    sortKey: 'adjustment_type',
    headerClassName: headerCenter('min-w-[8.5rem]'),
    className: cellCenter('whitespace-nowrap text-xs text-slate-700'),
    render: (row) =>
      display(row.adjustment_type ?? row.type ?? row.payment_type),
  },
  {
    id: 'payment_source',
    label: 'Source',
    sortKey: 'payment_source',
    headerClassName: headerCenter('min-w-[7.5rem]'),
    className: cellCenter('whitespace-nowrap text-xs text-slate-700'),
    render: (row) => display(row.payment_source),
  },
  {
    id: 'transaction_id',
    label: 'Txn Number',
    sortKey: 'transaction_id',
    headerClassName: headerCenter('min-w-[9.5rem]'),
    className: cellCenter(
      'whitespace-nowrap font-mono text-xs tabular-nums text-slate-700',
    ),
    render: (row) => display(row.transaction_id ?? row.auth_code),
  },
  {
    id: 'case_number',
    label: 'Case No.',
    sortKey: 'case_number',
    headerClassName: headerCenter('min-w-[7rem]'),
    className: cellCenter(
      'whitespace-nowrap font-mono text-xs tabular-nums text-slate-700',
    ),
    render: (row) => display(row.case_number),
  },
  {
    id: 'case_date',
    label: 'Case Date',
    sortKey: 'case_date',
    headerClassName: headerCenter('min-w-[7rem]'),
    className: cellCenter(
      'whitespace-nowrap text-xs tabular-nums text-slate-700',
    ),
    render: (row) => formatDate(row.case_date),
  },
  {
    id: 'chargeback_notification_date',
    label: 'Received',
    sortKey: 'chargeback_notification_date',
    headerClassName: headerCenter('min-w-[7rem]'),
    className: cellCenter(
      'whitespace-nowrap text-xs tabular-nums text-slate-700',
    ),
    render: (row) =>
      formatDate(row.chargeback_notification_date ?? row.created_at),
  },
  {
    id: 'adjustment_amount',
    label: 'CB Amount',
    sortKey: 'adjustment_amount',
    headerClassName: headerCenter('min-w-[7.5rem]'),
    className: cellCenter(
      'whitespace-nowrap font-mono text-xs tabular-nums font-semibold text-slate-900',
    ),
    render: (row) =>
      formatAmount(
        (row.adjustment_amount ?? row.payment_amount) as string | number,
      ),
  },
  {
    id: 'chargeback_status',
    label: 'Status',
    sortKey: 'chargeback_status',
    headerClassName: headerCenter('min-w-[8.5rem]'),
    className: cellCenter(
      'whitespace-nowrap text-xs font-semibold text-slate-700',
    ),
    render: (row) => display(row.payment_status ?? row.chargeback_status),
  },
  {
    id: 'deposit_date',
    label: 'Deposit Date',
    sortKey: 'funds_date',
    headerClassName: headerCenter('min-w-[7.5rem]'),
    className: cellCenter(
      'whitespace-nowrap text-xs tabular-nums text-slate-700',
    ),
    render: (row) =>
      String(row.payment_status ?? '') === 'WON_CREDIT'
        ? formatDate(row.funds_date ?? row.deposit_date)
        : '—',
  },
  {
    id: 'deposit_amount',
    label: 'Deposit Amt',
    sortKey: 'deposit_amount',
    headerClassName: headerCenter('min-w-[7.5rem]'),
    className: cellCenter(
      'whitespace-nowrap font-mono text-xs tabular-nums font-semibold text-slate-900',
    ),
    render: (row) =>
      String(row.payment_status ?? '') === 'WON_CREDIT'
        ? formatAmount(
            (row.adjustment_amount ?? row.deposit_amount) as string | number,
          )
        : '—',
  },
  {
    id: 'action',
    label: 'Action',
    className: cn(reportStyles.actionCell, 'text-center align-middle'),
    headerClassName: cn(reportStyles.actionCell, 'min-w-[5.5rem] text-center'),
    render: (row) => {
      const logId = row.pa_id ?? row.payment_adjustment_id
      const applicantIdValue = row.applicant_id ?? row.fk_applicant_id
      if (!logId) return '—'
      return (
        <span className={reportStyles.actionGroup}>
          <span className={reportStyles.actionSlot}>
            <Link
              to={`/reports/payment-logs/detail/${encodeURIComponent(String(logId))}`}
              className={reportStyles.actionIconBtn}
              title="View Log"
              aria-label="View Log"
            >
              <Eye aria-hidden />
            </Link>
          </span>
          <span className={reportStyles.actionSlot}>
            {applicantIdValue ? (
              <Link
                to={`/reports/chargeback/detail/${encodeURIComponent(String(applicantIdValue))}/${encodeURIComponent(String(logId))}`}
                className={reportStyles.actionIconBtn}
                title="Edit Log"
                aria-label="Edit Log"
              >
                <Pencil aria-hidden />
              </Link>
            ) : null}
          </span>
        </span>
      )
    },
  },
]
